import React, { useState, useEffect, useCallback } from "react";
import { showSuccess, showError } from "utils/notifications";
import FormSelect from "Common/Custom/FormSelect";
import FormInput from "Common/Custom/FormInput";
import FormTextarea from "Common/Custom/FormTextarea";
import Spinner from "Common/Globle/Spinner";
import { GetById, GetPaymentMethods, Create } from "adminApi/CustomersApi/CustomerRefund";
import { CashStack } from "react-bootstrap-icons";
/**
 * RefundModal
 * A Bootstrap modal that lets the user process a manual balance refund for a customer.
 *
 * Props:
 *   @param {Object|null}  customer     - Customer row from the table { customer_id, customer_name, available_balance, ... }
 *   @param {boolean}      show         - Controls modal visibility
 *   @param {Function}     onClose      - Called when the modal should close
 *   @param {Function}     onSuccess    - Called after a successful refund (triggers table refetch)
 */
const RefundModal = ({ customer, show, onClose, onSuccess }) => {
  // ── State ────────────────────────────────────────────────────────────────

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(false);

  const [form, setForm] = useState({
    payment_method_id: "",
    transaction_id: "",
    amount: "",
    remarks: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [liveBalance, setLiveBalance] = useState(null);

  // ── Reset + fetch data when modal opens ──────────────────────────────────
  useEffect(() => {
    if (!show || !customer) return;

    setForm({ payment_method_id: "", transaction_id: "", amount: "", remarks: "" });
    setErrors({});
    setLiveBalance(parseFloat(customer.available_balance) || 0);

    // Fetch fresh balance + payment methods in parallel
    setLoadingMethods(true);
    Promise.all([
      GetById(customer.customer_id),
      GetPaymentMethods(),
    ])
      .then(([detailRes, methodRes]) => {
        if (detailRes?.data?.data?.success) {
          setLiveBalance(parseFloat(detailRes.data.available_balance) || 0);
        }
        if (methodRes?.data?.success) {
          setPaymentMethods(
            (methodRes.data.data || []).map((m) => ({
              value: m.payment_method_id,
              label: m.payment_method_name,
            }))
          );
        }
      })
      .catch(() => { })
      .finally(() => setLoadingMethods(false));
  }, [show, customer]);

  // ── Computed after-refund balance ────────────────────────────────────────
  const parsedAmount = parseFloat(form.amount) || 0;
  const afterBalance = liveBalance !== null ? Math.max(liveBalance - parsedAmount, 0) : null;

  // ── Form change handler ──────────────────────────────────────────────────
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.payment_method_id) errs.payment_method_id = "Payment method is required";
    if (!form.amount || isNaN(parseFloat(form.amount)) || parseFloat(form.amount) <= 0) {
      errs.amount = "Enter a valid amount greater than 0";
    } else if (parseFloat(form.amount) > liveBalance) {
      errs.amount = `Amount cannot exceed available balance (${liveBalance?.toFixed(2)})`;
    }
    return errs;
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      const res = await Create({
        data: {
          customer_id: customer.customer_id,
          amount: parseFloat(form.amount),
          payment_method_id: form.payment_method_id,
          transaction_id: form.transaction_id || undefined,
          remarks: form.remarks || undefined,
        },
      });

      if (res?.data?.success) {
        showSuccess("Refund processed successfully!");
        onSuccess?.();
        onClose?.();
      } else {
        showError(res?.data?.error || "Failed to process refund. Please try again.");
      }
    } catch {
      showError("Unexpected error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show || !customer) return null;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={!submitting ? onClose : undefined}
      />

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ zIndex: 1055 }}
        aria-modal="true"
        aria-labelledby="refundModalLabel"
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
          <div className="modal-content border-0 shadow-lg" style={{ borderRadius: 16, overflow: "hidden" }}>

            {/* ── Header ── */}
            <div
              className="modal-header border-0 text-white px-4 py-3"
              style={{
                background:
                  "linear-gradient(135deg, var(--primary-color) 0%, var(--accent-color) 100%)",
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 42,
                    height: 42,
                    background: "#EEF2FF",
                    color: "#4F46E5",
                  }}
                >
                  <CashStack size={25} color="primary" />
                </div>
                <div>
                  <h5 className="modal-title mb-0 fw-semibold" id="refundModalLabel">
                    Process Refund
                  </h5>
                  <small className="opacity-75">Debit customer balance</small>
                </div>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                disabled={submitting}
                aria-label="Close"
              />
            </div>

            {/* ── Customer Summary Card ── */}
            <div className="px-4 pt-3 pb-0">
              <div
                className="rounded-3 p-3 d-flex align-items-center justify-content-between"
                style={{ background: "#f8f9ff", border: "1px solid #e3e6f0" }}
              >
                <div>
                  <div className="text-muted small mb-1">Customer</div>
                  <div className="fw-semibold text-dark" style={{ fontSize: 15 }}>
                    {customer.customer_name}
                  </div>
                  <div className="text-muted small">{customer.email || customer.customer_phone}</div>
                </div>
                <div className="text-end">
                  <div className="text-muted small mb-1">Available Balance</div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 20, color: "#198754" }}
                  >
                    ₹ {liveBalance !== null ? liveBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Form Body ── */}
            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-body px-4 pb-2 pt-3">
                <div className="row g-3">

                  {/* Payment Method */}
                  <FormSelect
                    field={{
                      name: "payment_method_id",
                      label: "Payment Method",
                      required: true,
                      fieldSize: "col-12",
                    }}
                    value={form.payment_method_id}
                    handleChange={handleChange}
                    options={paymentMethods}
                    placeholder="Select payment method"
                    error={errors.payment_method_id}
                    isLoading={loadingMethods}
                  />

                  {/* Transaction ID */}
                  <FormInput
                    field={{
                      name: "transaction_id",
                      label: "Transaction ID",
                      type: "text",
                      placeholder: "Enter transaction reference (optional)",
                      fieldSize: "col-12",
                    }}
                    value={form.transaction_id}
                    handleChange={handleChange}
                    error={errors.transaction_id}
                  />

                  {/* Amount */}
                  <FormInput
                    field={{
                      name: "amount",
                      label: "Refund Amount",
                      type: "number",
                      required: true,
                      placeholder: "Enter refund amount",
                      step: "0.01",
                      min: "0.01",
                      max: String(liveBalance || 0),
                      fieldSize: "col-12",
                    }}
                    value={form.amount}
                    handleChange={handleChange}
                    error={errors.amount}
                  />

                  {/* Live balance-after preview */}
                  {parsedAmount > 0 && liveBalance !== null && (
                    <div className="col-12">
                      <div
                        className="rounded-3 px-3 py-2 d-flex align-items-center justify-content-between"
                        style={{
                          background: parsedAmount > liveBalance ? "#fff5f5" : "#f0fff4",
                          border: `1px solid ${parsedAmount > liveBalance ? "#ffc9c9" : "#b2f2bb"}`,
                        }}
                      >
                        <span className="small text-muted">Balance after refund</span>
                        <span
                          className="fw-semibold"
                          style={{
                            fontSize: 15,
                            color: parsedAmount > liveBalance ? "#dc3545" : "#198754",
                          }}
                        >
                          ₹{" "}
                          {afterBalance !== null
                            ? afterBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })
                            : "—"}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Remarks */}
                  <FormTextarea
                    field={{
                      name: "remarks",
                      label: "Remarks",
                      placeholder: "Optional note about this refund",
                      rows: 3,
                      fieldSize: "col-12",
                    }}
                    value={form.remarks}
                    handleChange={handleChange}
                    error={errors.remarks}
                  />
                </div>
              </div>

              {/* ── Footer ── */}
              <div className="modal-footer border-0 px-4 pt-0 pb-4 gap-2">
                <button
                  type="button"
                  className="btn btn-light fw-semibold px-4"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn fw-semibold px-4 text-white"
                  disabled={submitting || loadingMethods}
                  style={{
                    background: "linear-gradient(135deg, var(--primary-color) 0%, var(--accent-color) 100%)",
                    border: "none",
                    minWidth: 120,
                  }}
                >
                  {submitting ? (
                    <span className="d-flex align-items-center justify-content-center gap-2">
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      Processing...
                    </span>
                  ) : (
                    <span className="d-flex align-items-center gap-1">
                      <i className="ti ti-check" />
                      Confirm Refund
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default RefundModal;
